import { useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link, useLocation } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { PublicFooter } from "@/components/PublicFooter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { getBrandConfig } from "@/config/brand";

interface LegalDocumentPageProps {
  title: string;
  description: string;
  content: string;
}

const legalLinks = [
  { href: "/privacy-policy", label: "Privacy Policy" },
  { href: "/terms-of-use", label: "Terms of Use" },
];

export const LegalDocumentPage = ({
  title,
  description,
  content,
}: LegalDocumentPageProps) => {
  const location = useLocation();
  const { brandName } = getBrandConfig();

  useEffect(() => {
    document.title = `${title} | ${brandName}`;
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [title, brandName]);

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col bg-background">
      <div className="absolute inset-0 gradient-ai-subtle opacity-30" />
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />

      <Navigation />

      <main className="relative flex-1">
        <div className="container mx-auto px-4 py-10 sm:py-14">
          <div className="mx-auto max-w-5xl space-y-6">
            <div className="space-y-4 text-center">
              <div className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.2em] text-primary">
                {brandName} Legal
              </div>
              <div className="space-y-3">
                <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
                  {title}
                </h1>
                <p className="mx-auto max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
                  {description}
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-3">
                {legalLinks.map((link) => (
                  <Button
                    key={link.href}
                    variant={location.pathname === link.href ? "default" : "outline"}
                    size="sm"
                    asChild
                  >
                    <Link to={link.href}>{link.label}</Link>
                  </Button>
                ))}
              </div>
            </div>

            <Card className="glass overflow-hidden">
              <div className="border-b border-border/60 bg-background/70 px-6 py-4 text-sm text-muted-foreground sm:px-8">
                This page reflects the current client-provided cloud legal document.
              </div>
              <div className="report-streaming !max-w-none !px-6 !pb-8 !pt-8 sm:!px-8 lg:!px-10 lg:!pb-10 lg:!pt-10">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    a: ({ href, ...props }) => {
                      const isExternal = href?.startsWith("http://") || href?.startsWith("https://");
                      return (
                        <a
                          href={href}
                          target={isExternal ? "_blank" : undefined}
                          rel={isExternal ? "noreferrer" : undefined}
                          {...props}
                        />
                      );
                    },
                  }}
                >
                  {content}
                </ReactMarkdown>
              </div>
            </Card>
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
};
